import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { getCachedData, invalidateCache, setCachedData } from '@/lib/cache';
import type { AdminUserSummaryResponse } from '@/types/admin';

export default function SendEmailPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [summary, setSummary] = useState<AdminUserSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();

    async function fetchSummary() {
      try {
        // Check cache first
        const cacheKey = `view_${userId}_summary`;
        const cachedData = getCachedData<AdminUserSummaryResponse>(cacheKey);

        if (cachedData) {
          setSummary(cachedData);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        const response = await authenticatedFetch(`users/${userId}/summary`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          const message =
            typeof body?.error === 'string'
              ? body.error
              : `Failed to fetch user summary (${response.status})`;
          throw new Error(message);
        }

        const payload = (await response.json()) as AdminUserSummaryResponse;
        if (!controller.signal.aborted) {
          setSummary(payload);
          // Store in cache
          setCachedData(cacheKey, payload);
        }
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Failed to load user summary',
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchSummary();

    return () => controller.abort();
  }, [authenticatedFetch, userId]);

  const handleSendEmail = async () => {
    if (!userId || !subject.trim() || !body.trim()) {
      setSendResult('Subject and body are required');
      return;
    }

    try {
      setSending(true);
      setSendResult(null);

      // Parse CC addresses (comma-separated)
      const ccArray = cc
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

      const response = await authenticatedFetch('sendEmail', {
        method: 'POST',
        body: JSON.stringify({
          userId,
          subject: subject.trim(),
          body: body.trim(),
          ...(ccArray.length > 0 && { cc: ccArray }),
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const message =
          typeof body?.error === 'string'
            ? body.error
            : `Failed to send email (${response.status})`;
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));
      const message =
        typeof result?.message === 'string'
          ? result.message
          : 'Email sent successfully';
      setSendResult(message);

      // Invalidate cache so notifications are loaded fresh from the DB
      if (userId) {
        invalidateCache(`view_${userId}_summary`);
      }

      // Clear form on success
      setSubject('');
      setBody('');
      setCc('');
    } catch (sendError) {
      console.error(sendError);
      setSendResult(
        sendError instanceof Error
          ? sendError.message
          : 'Unable to send email.',
      );
    } finally {
      setSending(false);
    }
  };

  const email = summary?.contact?.email;

  if (loading) {
    return (
      <div className="container">
        <p>Loading user...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <p className="text-destructive">Error: {error}</p>
        <Link to={`/users/${userId}`} className="text-primary hover:underline">
          ← Back to user
        </Link>
      </div>
    );
  }

  if (!summary || !email) {
    return (
      <div className="container">
        <p>User not found or email not available</p>
        <Link to="/users" className="text-primary hover:underline">
          ← Back to users
        </Link>
      </div>
    );
  }

  return (
    <div className="container space-y-3 sm:space-y-6">
      <Breadcrumb
        items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Users', to: '/users' },
          { label: userId || 'User', to: `/users/${userId}` },
          { label: 'Send Email' },
        ]}
      />
      <div className="flex items-center gap-2 sm:gap-4">
        <Link to={`/users/${userId}`} className="text-primary hover:underline">
          ← Back to user
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Send Custom Email</CardTitle>
          <CardDescription>Send an email to {email}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">To</Label>
            <Input
              id="email"
              type="email"
              value={email}
              disabled
              className="bg-muted"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">
              Subject <span className="text-destructive">*</span>
            </Label>
            <Input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              disabled={sending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">
              Body <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Email body (plain text)"
              rows={10}
              disabled={sending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cc">CC (optional)</Label>
            <Input
              id="cc"
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="Comma-separated email addresses"
              disabled={sending}
            />
            <p className="text-sm text-muted-foreground">
              Separate multiple addresses with commas
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              onClick={handleSendEmail}
              disabled={sending || !subject.trim() || !body.trim()}
            >
              {sending ? 'Sending…' : 'Send Email'}
            </Button>
            {sendResult && (
              <span
                className={`text-sm ${
                  sendResult.includes('successfully')
                    ? 'text-green-600'
                    : 'text-destructive'
                }`}
              >
                {sendResult}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
